import React, {useEffect, useState, useRef} from 'react'
import {Video, CtaButton} from '_uiComponents'
import {VStack, Box, Text, useMultiStyleConfig, Link} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {buildVideoSources, updateCloudinaryPDPoster, createRedirectUrl, buildEditorialVideoAnalyticsTitle, PAGE_DESIGNER_MODULES} from '_utils'
import {usePageDesigner, useIsElementInViewport, useAnalytics, useViewport, useOpenDrawer} from '_customHooks'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {CLDVideoAsset} from '_types'

import AOS from 'aos'
import 'aos/dist/aos.css'

export interface WallMixedVideoProps {
    iframe_id?: CLDVideoAsset
    iframem_id?: CLDVideoAsset
    video_link?: string
    title?: string
    text?: string
    text_align?: string
    h_align?: string
    v_align?: string
    ctaText?: string
    ctaStyle?: 'button' | 'text' | 'text-cta' | 'outline' | 'blank'
    animation_enabled?: boolean
    animation_cta_text?: boolean
    animation_text?: boolean
    animation_duration?: string
    video_padding?: string
    button_background?: string
    layout_text_below_image: boolean
    content_color?: string
    button_color?: string
    productID?: string
    position: number
    analyticsID?: string
    analyticsList?: string
    listingLayout?: string
    mobileSideBySide?: boolean
    analyticsName?: string
    isActiveSlide?: boolean
    adaptiveHeight?: boolean
    parentColsNumber?: string
    horizontal_index?: number
}

const WallMixedVideo: React.FC<WallMixedVideoProps> = ({
    iframe_id,
    iframem_id,
    title,
    text,
    text_align,
    h_align,
    v_align,
    ctaText,
    ctaStyle,
    animation_enabled,
    animation_cta_text,
    animation_text,
    animation_duration,
    video_padding,
    button_background,
    layout_text_below_image,
    content_color,
    button_color,
    productID,
    video_link,
    position,
    analyticsID,
    analyticsList,
    listingLayout,
    mobileSideBySide,
    analyticsName,
    isActiveSlide = true,
    adaptiveHeight,
    parentColsNumber,
    horizontal_index,
}) => {
    const {linkObject, globalVideoTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const {handleShopTheImageClick} = useOpenDrawer()
    const [alreadySentProductImpression, setAlreadySentProductImpression] = useState(false)
    const targetElement = useRef(null)
    const isInViewport = useIsElementInViewport(targetElement)
    const {isMobile} = useViewport()
    const {analyticsEventPromoClick, analyticsProductClick, analyticsProductImpression} = useAnalytics()
    const {formatMessage} = useIntl()
    const {data: product} = useProduct(
        {
            parameters: {
                id: productID!,
            },
        },
        {
            enabled: !!productID,
        },
    )
    const [moduleLink, setModuleLink] = useState(video_link ? createRedirectUrl(video_link, linkObject.hostlink, linkObject.locale, linkObject.site) : '')
    useEffect(() => {
        if (product && product.slugUrl) {
            setModuleLink(createRedirectUrl(product.slugUrl, linkObject.hostlink, linkObject.locale, linkObject.site))
        }
    }, [product])

    const ratioDesktop =
        iframe_id?.formValues?.video?.asset?.height && iframe_id?.formValues?.video?.asset?.width
            ? iframe_id.formValues.video.asset.width / iframe_id.formValues.video.asset.height
            : 0

    const ratioMobile =
        iframem_id?.formValues?.video?.asset?.height && iframem_id?.formValues?.video?.asset?.width
            ? iframem_id.formValues.video.asset.width / iframem_id.formValues.video.asset.height
            : 0

    useEffect(() => {
        if (isInViewport && !alreadySentProductImpression && product && isActiveSlide) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout,
                impressions: analyticsProductObject,
            })

            setAlreadySentProductImpression(true)
        }
    }, [isInViewport, product])

    const triggerAnalyticsEventProductClick = (): void => {
        if (product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position - 1})

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout,
                actionField: {
                    list: analyticsList ? analyticsList : '',
                },
                product: analyticsProductObject,
            })
        }
    }

    const triggerAnalyticsEventPromoClick = (textBtn: string): void => {
        const analyticsCheck = productID ? productID : analyticsID
        analyticsEventPromoClick({
            id: analyticsCheck ? analyticsCheck : 'null',
            position: horizontal_index !== undefined ? String(position) + '-' + String(horizontal_index + 1) : String(position),
            creative: PAGE_DESIGNER_MODULES.WALL_MIXED_VIDEO,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    // Video analytics (play/pause/progress) are centralized in the atom `Video`, emitted only
    // when controls=true (SFRA parity). The atom receives the analyticsTitle built below.
    const videoAnalyticsTitle = buildEditorialVideoAnalyticsTitle(formatMessage, (position ?? 0) + 1, (horizontal_index ?? 0) + 1)

    const getPaddingValues = (video_padding?: string) => {
        if (!video_padding) return {top: 0, right: 0, bottom: 0, left: 0}
        const parts = video_padding.split(' ').map((v) => parseInt(v.replace('px', ''), 10) || 0)
        if (parts.length === 1) {
            return {top: parts[0], right: parts[0], bottom: parts[0], left: parts[0]}
        }
        if (parts.length === 2) {
            return {top: parts[0], right: parts[1], bottom: parts[0], left: parts[1]}
        }
        return {
            top: parts[0],
            right: parts[1],
            bottom: parts[2] || parts[0],
            left: parts[3] || parts[1],
        }
    }
    // animation_enabled it's for image
    // animation_text animation for title
    // animation_cta_text animation for cta only if his style is text
    useEffect(() => {
        if (animation_enabled || animation_text || animation_cta_text) {
            AOS.init()
        }
    }, [])
    const videoPadding = getPaddingValues(video_padding)

    const threeCols = parentColsNumber === '3'
    // Cell width depends on the number of columns in the row: 3-col → ≈33vw, otherwise ≈25vw (4-col).
    const videoSlot = threeCols ? 'wallMixed3Col' : 'wallMixed4Col'

    const styles = useMultiStyleConfig('WallMixedVideo', {
        text_align,
        h_align,
        v_align,
        videoPadding,
        button_background,
        content_color,
        button_color,
        layout_text_below_image,
        ratioDesktop,
        ratioMobile,
        mobileSideBySide,
        threeCols,
    }) as Record<string, any>
    const playerDeskConf = iframe_id && JSON.parse(iframe_id.playerConf)
    const playerMobileConf = iframem_id && JSON.parse(iframem_id.playerConf)

    const isControlDesk = playerDeskConf && playerDeskConf.playerConfig?.controls
    const isAutoplayDesk = playerDeskConf && playerDeskConf.playerConfig?.autoplay
    const isLoopDesk = playerDeskConf && playerDeskConf.playerConfig?.loop
    const isMuteDesk = playerDeskConf && playerDeskConf.playerConfig?.muted
    const isControlMobile = playerMobileConf && playerMobileConf.playerConfig?.controls
    const isAutoplayMobile = playerMobileConf && playerMobileConf.playerConfig?.autoplay
    const isLoopMobile = playerMobileConf && playerMobileConf.playerConfig?.loop
    const isMuteMobile = playerMobileConf && playerMobileConf.playerConfig?.muted

    const isModuleClickable = !!moduleLink && ((isMobile && (playerMobileConf ? !isControlMobile : !isControlDesk)) || (!isMobile && !isControlDesk))

    const ctaVariantMap = {
        button: 'button',
        outline: 'outline',
        text: 'textBorderTop',
        'text-cta': 'text',
    } as const
    const ctaVariant = ctaStyle && ctaStyle in ctaVariantMap ? ctaVariantMap[ctaStyle] : undefined

    const isWrappedLink = !layout_text_below_image

    const getCtaColor = (): string => {
        if (layout_text_below_image) {
            return ctaStyle === 'button' ? 'color.text.white' : 'color.text.black'
        } else {
            return 'color.text.white'
        }
    }

    const TitleBlock = (
        <VStack {...(layout_text_below_image ? styles.titleContainerBelow : styles.titleContainer)}>
            {title && (
                <Text
                    as="h2"
                    {...(layout_text_below_image ? styles.titleBelow : styles.title)}
                    {...(animation_text
                        ? {
                              'data-aos': 'fade-left',
                              'data-aos-duration': animation_duration ? animation_duration : '1200',
                          }
                        : {})}
                >
                    {title}
                </Text>
            )}
            {text && <Text {...(layout_text_below_image ? styles.textBelow : styles.text)}>{text}</Text>}
            {ctaStyle !== 'blank' && ctaText && (
                <Box>
                    {/* empty wrapper fixes multi-line CTA text underline */}
                    <CtaButton
                        variant={ctaVariant}
                        href={isWrappedLink ? '' : moduleLink}
                        ctaColor={getCtaColor()}
                        background="color.text.black"
                        onClick={(e) => {
                            if (layout_text_below_image) {
                                if (handleShopTheImageClick(e, video_link)) return
                                if (productID && product) triggerAnalyticsEventProductClick()
                                triggerAnalyticsEventPromoClick('')
                            }
                        }}
                        {...(animation_text
                            ? {
                                  'data-aos': 'animationCtaText',
                                  'data-aos-offset': '0',
                                  'data-aos-once': 'false',
                              }
                            : {})}
                    >
                        {ctaText}
                    </CtaButton>
                </Box>
            )}
        </VStack>
    )

    // FIX: linkProps avoids defining WrapperLink inside render, which causes React to unmount/remount
    // children (including Video) on every re-render → video flickering.
    const linkProps = {
        'data-testid': 'wall-mixed-video-full-link',
        className: 'cc-link-full',
        href: moduleLink || undefined,
        title: '',
        'aria-label': 'wall banner link',
        'data-disabled': !isModuleClickable,
        ...styles.link,
        onClick: (e) => {
            if (!isModuleClickable) {
                e.preventDefault()
                return
            }
            if (handleShopTheImageClick(e, video_link)) return
            if (productID && product) triggerAnalyticsEventProductClick()
            triggerAnalyticsEventPromoClick('')
        },
    }

    return (
        <Link {...linkProps}>
            <VStack {...styles.boxWrapper} data-testid="wall-mixed-video-wrapper" ref={targetElement}>
                <Box {...styles.videoWrapper}>
                    {isMobile ? (
                        <Video
                            overflow="hidden"
                            height="100%"
                            isVideoModule={true}
                            autoplay={playerMobileConf ? isAutoplayMobile : isAutoplayDesk}
                            controls={playerMobileConf ? isControlMobile : isControlDesk}
                            muted={playerMobileConf ? isMuteMobile : isMuteDesk}
                            loop={playerMobileConf ? isLoopMobile : isLoopDesk}
                            sources={
                                iframem_id
                                    ? buildVideoSources(iframem_id, {slot: videoSlot, transformation: globalVideoTransformation, cname: CloudinaryPageDesignerCNAME})
                                    : buildVideoSources(iframe_id, {slot: videoSlot, transformation: globalVideoTransformation, cname: CloudinaryPageDesignerCNAME})
                            }
                            poster={
                                // Match the poster to the asset this <Video> actually renders: the mobile
                                // asset when present, else the desktop asset as a genuine fallback (same
                                // source selection as `sources` above). No cross-asset `||` forcing a
                                // second poster derivation for an unused asset.
                                // 'mobile' variant: this is the mobile <Video>, so the poster is sized
                                // for the mobile box (w_440) even when the source falls back to the
                                // desktop asset — it is still shown in the mobile box.
                                iframem_id
                                    ? updateCloudinaryPDPoster(iframem_id, globalVideoTransformation, CloudinaryPageDesignerCNAME, 'mobile')
                                    : iframe_id
                                      ? updateCloudinaryPDPoster(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME, 'mobile')
                                      : ''
                            }
                            objectFit="cover"
                            analyticsTitle={videoAnalyticsTitle}
                            {...styles.video}
                        ></Video>
                    ) : (
                        <Video
                            overflow="hidden"
                            height="100%"
                            isVideoModule={true}
                            autoplay={isAutoplayDesk}
                            controls={isControlDesk}
                            muted={isMuteDesk}
                            loop={isLoopDesk}
                            sources={buildVideoSources(iframe_id, {slot: videoSlot, transformation: globalVideoTransformation, cname: CloudinaryPageDesignerCNAME})}
                            poster={iframe_id ? updateCloudinaryPDPoster(iframe_id, globalVideoTransformation, CloudinaryPageDesignerCNAME) : ''}
                            objectFit="cover"
                            analyticsTitle={videoAnalyticsTitle}
                            {...styles.video}
                        ></Video>
                    )}
                </Box>
                {<>{TitleBlock}</>}
            </VStack>
        </Link>
    )
}

const MemoizedWallMixedVideo = React.memo(WallMixedVideo)

export {MemoizedWallMixedVideo as WallMixedVideo}
